import { useCallback, useMemo, useState } from 'react';

import { apiService } from '../../services';
import type {
  Invoice,
  InvoiceDetail,
  PaymentInvoiceListResponse,
  PaymentInvoiceListParams,
  PaymentInvoiceCompetenceParams,
  PaymentSummary,
  UseVidaPaymentReturn,
} from '../../types';
import type { PaymentInvoiceCompetenceItemDTO } from '../../types/vida/payment/paymentType';
import {
  mapInvoiceListItemToInvoice,
  mapInvoiceDetailItemToInvoiceDetail,
} from '../../types/vida/payment/paymentType';
import type { CompetenciaDropdownOption } from '../../utils/paymentCompetenciaUtil';
import {
  buildCompetenciaDropdownOptions,
  filterCompetenciasUltimosMeses,
  PAYMENT_TAB_COMPETENCIA_MESES,
} from '../../utils/paymentCompetenciaUtil';

export const useVidaPayment = (): UseVidaPaymentReturn => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [summary, setSummary] = useState<PaymentSummary>({
    monthlyAmount: 0,
    insureds: 0,
    nextDueDate: '',
    status: 'Em dia'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceDetail | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(6);
  const [competenciaItems, setCompetenciaItems] = useState<PaymentInvoiceCompetenceItemDTO[]>([]);
  const [competenciaSubestipulanteFilter, setCompetenciaSubestipulanteFilterState] = useState<
    number | undefined
  >(undefined);

  const competenciaDropdownOptions = useMemo(
    (): CompetenciaDropdownOption[] =>
      buildCompetenciaDropdownOptions(competenciaItems, competenciaSubestipulanteFilter),
    [competenciaItems, competenciaSubestipulanteFilter]
  );

  const selectInvoice = async (invoice: Invoice) => {
    const identCobrancaObt = parseInt(invoice.id, 10);
    const policyNumber = invoice.policyNumber;

    const response = await apiService.payment.getPaymentInvoiceDetail(identCobrancaObt);

    const firstItem = response?.pagina?.content?.[0];
    if (firstItem) {
      const detail = mapInvoiceDetailItemToInvoiceDetail(firstItem, policyNumber);
      setSelectedInvoice(detail);
    } else {
      throw new Error('No invoice detail data received');
    }
  };

  const closeModal = () => {
    setSelectedInvoice(null);
  };

  const applyHistoryResponses = (responses: PaymentInvoiceListResponse[]) => {
    const safeResponses = responses.filter(Boolean);
    const firstResponse = safeResponses[0];
    const mergedContent = safeResponses.flatMap((res) => res.pagina?.content ?? []);
    const hasPendingStatus = safeResponses.some((res) => res.possuiPendenciaStatus);
    const totalMonthlyAmount = safeResponses.reduce((acc, res) => acc + (res.valorTotalMesAtual ?? 0), 0);
    const nextDueDate = firstResponse?.proximoVencimento ?? '';

    setSummary({
      monthlyAmount: totalMonthlyAmount,
      insureds: 0,
      nextDueDate: nextDueDate
        ? (() => {
          try {
            const [year, month, day] = nextDueDate.split('-').map(Number);
            const date = new Date(year, (month || 1) - 1, day || 1);
            return date.toLocaleDateString('pt-BR');
          } catch {
            return nextDueDate;
          }
        })()
        : '',
      status: hasPendingStatus ? 'Atenção' : 'Em dia'
    });
    setInvoices(mergedContent.map(mapInvoiceListItemToInvoice));
    setCurrentPage(0);
    setTotalPages(mergedContent.length > 0 ? 1 : 0);
    setTotalElements(mergedContent.length);
    setPageSize(mergedContent.length || 6);
  };

  const getPaymentInvoiceHistory = useCallback(async (
    params: PaymentInvoiceListParams
  ): Promise<PaymentInvoiceListResponse> => {
    setIsLoading(true);
    try {
      const result = await apiService.payment.getPaymentInvoiceHistory(params);

      applyHistoryResponses([result]);

      return result;
    } catch (err) {
      console.error('[useVidaPayment] Error in getPaymentInvoiceHistory:', err);
      setInvoices([]);
      setSummary({
        monthlyAmount: 0,
        insureds: 0,
        nextDueDate: '',
        status: 'Em dia'
      });
      setCurrentPage(0);
      setTotalPages(0);
      setTotalElements(0);
      setPageSize(6);
      const emptyResult: PaymentInvoiceListResponse = {
        mensagem: '',
        possuiPendenciaStatus: false,
        proximoVencimento: '',
        valorTotalMesAtual: 0,
        pagina: {
          content: [],
          pageNumber: 0,
          pageSize: 6,
          totalElements: 0,
          totalPages: 0
        }
      };
      return emptyResult;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /** Carrega todas as competências da API (dropdown + filtro por subestipulante). */
  const loadPaymentInvoiceCompetencias = useCallback(async (
    params: PaymentInvoiceCompetenceParams
  ): Promise<PaymentInvoiceCompetenceItemDTO[]> => {
    try {
      const response = await apiService.payment.getPaymentInvoiceCompetence(params);
      const items = response?.retorno ?? [];
      setCompetenciaItems(items);
      setCompetenciaSubestipulanteFilterState(undefined);
      return items;
    } catch (err) {
      console.error('[useVidaPayment] Error in loadPaymentInvoiceCompetencias:', err);
      setCompetenciaItems([]);
      return [];
    }
  }, []);

  const applyCompetenciaSubestipulanteFilter = useCallback((identificadorSubestipulante?: number) => {
    setCompetenciaSubestipulanteFilterState(identificadorSubestipulante);
  }, []);

  /**
   * Competências dos últimos 4 meses (MM-yyyy) para carga inicial de lista-faturas.
   * Usa itens já carregados ou busca na API se necessário.
   */
  const getPaymentInvoiceCompetenciaFaturas = useCallback(async (
    params: PaymentInvoiceCompetenceParams
  ): Promise<string[]> => {
    try {
      let items = competenciaItems;
      const needsFetch =
        items.length === 0 || items.some((item) => item.identContrato !== params.identContrato);

      if (needsFetch) {
        items = await loadPaymentInvoiceCompetencias(params);
      }

      const competenciasApi = items
        .map((item) => item.competenciaFatura?.trim())
        .filter((competencia): competencia is string => Boolean(competencia));

      if (competenciasApi.length === 0) return [];

      return filterCompetenciasUltimosMeses(competenciasApi, PAYMENT_TAB_COMPETENCIA_MESES);
    } catch (err) {
      console.error('[useVidaPayment] Error in getPaymentInvoiceCompetenciaFaturas:', err);
      return [];
    }
  }, [competenciaItems, loadPaymentInvoiceCompetencias]);

  const getPaymentInvoiceHistoryByCompetencias = useCallback(async (
    params: Omit<PaymentInvoiceListParams, 'competenciaFatura'> & { competenciasFatura: string[] }
  ): Promise<PaymentInvoiceListResponse[]> => {
    const { competenciasFatura, ...baseParams } = params;
    setIsLoading(true);
    try {
      const responses: PaymentInvoiceListResponse[] = [];
      for (const competenciaFatura of competenciasFatura) {
        const response = await apiService.payment.getPaymentInvoiceHistory({
          ...baseParams,
          competenciaFatura,
        });
        responses.push(response);
      }

      applyHistoryResponses(responses);
      return responses;
    } catch (err) {
      console.error('[useVidaPayment] Error in getPaymentInvoiceHistoryByCompetencias:', err);
      setInvoices([]);
      setSummary({
        monthlyAmount: 0,
        insureds: 0,
        nextDueDate: '',
        status: 'Em dia'
      });
      setCurrentPage(0);
      setTotalPages(0);
      setTotalElements(0);
      setPageSize(6);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    summary,
    invoices,
    isLoading,
    selectedInvoice,
    setSelectedInvoice,
    selectInvoice,
    closeModal,
    getPaymentInvoiceHistory,
    getPaymentInvoiceHistoryByCompetencias,
    loadPaymentInvoiceCompetencias,
    getPaymentInvoiceCompetenciaFaturas,
    applyCompetenciaSubestipulanteFilter,
    competenciaDropdownOptions,
    currentPage,
    totalPages,
    totalElements,
    pageSize
  };
};

